/**
 * Pinyin helpers for the tone and pinyin→hanzi exercises. Everything works on single syllables
 * written with tone marks ("mǎ"); the four marks are the only combining characters touched, so
 * ü survives a round trip.
 */
import type { Character, Word } from '@ezishizi/content-schema';
import { pickN, shuffle, type Rng } from './rng.ts';

/** Combining marks for tones 1–4, indexed by tone. Index 0 (neutral) has no mark. */
const MARKS = ['', '̄', '́', '̌', '̀'] as const;
const MARK_RE = /[̄́̌̀]/g;
const DIAERESIS = '̈';

export type Tone = 0 | 1 | 2 | 3 | 4;

/** "mǎ" → "ma", "lǜ" → "lü". Lower-cased. */
export function baseSyllable(syllable: string): string {
  return syllable.normalize('NFD').replace(MARK_RE, '').normalize('NFC').toLowerCase();
}

/** Which tone a syllable carries; 0 when it has no mark (neutral tone). */
export function toneOf(syllable: string): Tone {
  const d = syllable.normalize('NFD');
  for (const t of [1, 2, 3, 4] as const) if (d.includes(MARKS[t])) return t;
  return 0;
}

/**
 * Put the tone mark on the right vowel of a toneless syllable: a or e if present, the o of ou,
 * otherwise the last vowel. Tone 0 returns the base unchanged.
 */
export function withTone(base: string, tone: Tone): string {
  const b = baseSyllable(base);
  if (tone === 0) return b;
  const d = b.normalize('NFD'); // ü is now "u" + diaeresis
  let at = d.indexOf('a');
  if (at === -1) at = d.indexOf('e');
  if (at === -1 && d.includes('ou')) at = d.indexOf('o');
  if (at === -1) {
    for (let i = d.length - 1; i >= 0; i--) {
      if ('aeiou'.includes(d[i]!)) {
        at = i;
        break;
      }
    }
  }
  if (at === -1) return b;
  let insertAt = at + 1;
  if (d[insertAt] === DIAERESIS) insertAt++;
  return (d.slice(0, insertAt) + MARKS[tone] + d.slice(insertAt)).normalize('NFC');
}

/**
 * The syllable a character has inside a specific teaching word ("木马" → "mù" for 木), falling
 * back to the character's most common dictionary reading. Words carry one space-separated
 * syllable per character, which is what makes the positional lookup safe.
 */
export function syllableInWord(
  char: string,
  word: Pick<Word, 'text' | 'pinyin'> | null,
  character: Pick<Character, 'pinyin'> | undefined,
): string | null {
  if (word) {
    const chars = [...word.text];
    const syllables = word.pinyin.trim().split(/\s+/);
    if (chars.length === syllables.length) {
      const i = chars.indexOf(char);
      if (i >= 0 && syllables[i]) return syllables[i]!;
    }
  }
  return character?.pinyin[0] ?? null;
}

/**
 * Four tone choices for a syllable: all of tones 1–4 when the target carries one, or the
 * neutral form plus three toned variants when the target is neutral. Shuffled; always
 * includes the target.
 */
export function toneChoices(target: string, rng: Rng): string[] {
  const base = baseSyllable(target);
  const t = toneOf(target);
  const toned = ([1, 2, 3, 4] as const).map((k) => withTone(base, k));
  if (t === 0) return shuffle([base, ...pickN(toned, 3, rng)], rng);
  return shuffle(toned, rng);
}
